/**
 * ArticleRelated (G2, D9/D10, PR14): "Readers also read" from articleRuntime.related (written by the G3
 * related refresh). G3 contract: the root is aside[data-seg-related="<section.id>"], it never contains a
 * nested <aside>, and the builder wraps it in <!--seg:related:start-->…<!--seg:related:end--> at publish.
 * With no items the region is an empty hidden aside (its section collapses through the style block, M8).
 * Card meta line (TOKENS.md:96 card-meta, Q5 option A): the item's topic and/or dateModified (formatted
 * 'en', UTC — M6) joined by " · "; nothing when both are absent; the SEO description is never rendered.
 * Card image = item.imageUrl (the article cover). B8.1 (designer G5-M1, owner 2026-09-26) reverted G5 R4's
 * thumbnailUrl: the `small` sibling (origin / 3) is soft in the 348 px card on DPR ≥ 2 screens.
 * thumbnailUrl stays in the runtime for a future srcset.
 */
import React, { memo } from 'react';
import type { ArticleBlockProps } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/types';
import { articleLinkTargetProps } from '@modules/screen-view-v2/utils/articleLinks';
import { formatArticleDate } from '@modules/screen-view-v2/utils/articleDate';
import { slugifyHeading } from '@modules/screen-view-v2/utils/articleSlug';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';
import type { ArticleRelatedRuntimeItem } from '@modules/screen-view-v2/types/articleRuntime';

function cardMeta(item: ArticleRelatedRuntimeItem): string {
  const date = item.dateModified ? formatArticleDate(item.dateModified, 'en') : null;
  return [item.topic, date].filter((part): part is string => Boolean(part)).join(' · ');
}

const DEFAULT_LIMIT = 3;

export const ArticleRelated = memo(function ArticleRelated({ section, locale, article }: ArticleBlockProps): React.ReactElement {
  const content = section.articleRelatedContent;
  const limit = content?.limit && content.limit > 0 ? content.limit : DEFAULT_LIMIT;
  const items = (article.runtime?.related?.items ?? []).slice(0, limit);
  if (items.length === 0) {
    return <aside className="ap-block ap-related" data-seg-related={section.id} hidden />;
  }
  const heading = resolveLocaleText(content?.heading, locale);
  const headingId = `ap-${slugifyHeading(section.id)}-heading`;
  const siteOrigin = article.runtime?.siteOrigin;
  return (
    <aside className="ap-block ap-related" data-seg-related={section.id} aria-labelledby={heading ? headingId : undefined}>
      {heading ? <h2 id={headingId} className="ap-panel-title">{heading}</h2> : null}
      <ul className="ap-related-grid">
        {items.map((item) => {
          const meta = cardMeta(item);
          const cover = item.imageUrl;
          return (
            <li key={item.alias}>
              <a className="ap-card" href={item.url} {...articleLinkTargetProps(item.url, undefined, siteOrigin)}>
                {cover
                  ? <img className="ap-card-cover" src={cover} alt="" loading="lazy" />
                  : <span className="ap-card-cover" aria-hidden="true" />}
                <div className="ap-card-body">
                  {meta ? <p className="ap-card-meta">{meta}</p> : null}
                  <h3 className="ap-card-title">{item.title}</h3>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </aside>
  );
});

ArticleRelated.displayName = 'ArticleRelated';
